import { useCallback, useEffect, useRef, useState } from 'react';
import { signalRService } from '../services/signalr/SignalRService';
import { HubEvents } from '../services/signalr/types';
import { useRooms } from '../context/RoomContext';
import { Room } from '../types/user';
import { UnityGameState } from '../types';
import { roomInviteService } from '../services/api/roomInviteService';

export interface IncomingInvite {
  inviteId: string;
  roomId: string;
  roomName: string;
  fromName: string;
  expiresAt: number;
}

export interface TravelState {
  roomName: string;
  error?: string | null;
}

interface InviteUser {
  id: string;
  displayName: string;
  avatar?: string;
  gender?: string;
  characterClass?: string;
}

/**
 * P3 — the in-game room-invite + time-travel controller. Subscribes to the
 * targeted room_invite_* envelopes (delivered via SocialEventReceived, the
 * call_* pattern), surfaces the invitee banner, and orchestrates the leave→join
 * "time travel" when an invite is accepted (both parties receive room_invite_go).
 *
 * The server has already stamped a consume-once spawn override + entry token for
 * the target room, so the ordinary join flow lands the pair at the inviter's last
 * spot there. This hook only drives the client leave→join (no server room switch
 * exists). Needs a live two-client run to certify — see the P3 QA list.
 */
export function useRoomInvites(params: {
  currentRoom: Room | null;
  user: InviteUser | null;
  gameState: UnityGameState;
}) {
  const { currentRoom, user, gameState } = params;
  const { joinRoom, leaveRoom, setCurrentRoom, rooms, refreshRooms } = useRooms();

  const [incomingInvite, setIncomingInvite] = useState<IncomingInvite | null>(null);
  const [travel, setTravel] = useState<TravelState | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const travelStartRef = useRef(0);
  const currentRoomRef = useRef(currentRoom);
  const userRef = useRef(user);
  const roomsRef = useRef(rooms);
  useEffect(() => { currentRoomRef.current = currentRoom; }, [currentRoom]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);

  const showNote = useCallback((text: string) => {
    setNote(text);
    setTimeout(() => setNote(null), 4000);
  }, []);

  const sendBridge = (type: string, data: Record<string, unknown>) => {
    const w = window as Window & { sendFlexoMessage?: (t: string, d: unknown) => void };
    w.sendFlexoMessage?.(type, data);
  };

  const findRoom = (roomId: string): Room | undefined => roomsRef.current.find((r) => r.id === roomId);

  // ---- Travel orchestration (client leave → join; overlay ≥1.6 s) ----
  const startTravel = useCallback(async (roomId: string, roomName: string) => {
    const room = currentRoomRef.current;
    const u = userRef.current;
    if (!room || !u) return;

    setIncomingInvite(null);
    setTravel({ roomName });
    travelStartRef.current = Date.now();

    // Cosmetic warp tint on the Unity veil.
    sendBridge('travel_mode', { on: true });

    const rollback = async () => {
      setTravel({ roomName, error: 'Couldn’t reach that room — returning you back.' });
      try {
        await joinRoom(room.id, u.id, u.displayName, u.avatar,
          `${u.characterClass ?? 'tank'}_${u.gender === 'female' ? 'Girl' : 'Boy'}`, u.gender);
        setCurrentRoom(room);
      } catch { /* best effort */ }
      setTimeout(() => setTravel(null), 2200);
    };

    try {
      // Leave the old SignalR room group.
      await leaveRoom(room.id, u.id);

      // Resolve the target Room (refresh + short poll — invited rooms are joinable).
      let target = findRoom(roomId);
      if (!target) {
        await refreshRooms();
        for (let i = 0; i < 10 && !target; i++) {
          target = findRoom(roomId);
          if (target) break;
          await new Promise((r) => setTimeout(r, 200));
        }
      }

      const characterType = `${u.characterClass ?? 'tank'}_${u.gender === 'female' ? 'Girl' : 'Boy'}`;
      const ok = await joinRoom(roomId, u.id, u.displayName, u.avatar, characterType, u.gender);
      if (!ok) { await rollback(); return; }

      target = target ?? findRoom(roomId);
      if (!target) { await rollback(); return; }

      // Changing currentRoom recomputes gameParams → UnityGame re-sends bridge joinRoom
      // → Unity rebuilds the target scene → MySpawnResult delivers the override spawn.
      setCurrentRoom(target);
      // Overlay unmounts when gameState returns to in_game (min 1.6 s enforced below).
    } catch (e) {
      console.error('[RoomInvite] travel failed:', e);
      await rollback();
    }
  }, [joinRoom, leaveRoom, setCurrentRoom, refreshRooms]);

  // Unmount the overlay once the target room reaches in_game (respecting the 1.6 s floor).
  useEffect(() => {
    if (!travel || travel.error) return;
    if (gameState !== 'in_game') return;
    const elapsed = Date.now() - travelStartRef.current;
    const wait = Math.max(0, 1600 - elapsed);
    const t = setTimeout(() => {
      setTravel(null);
      sendBridge('travel_mode', { on: false });
    }, wait);
    return () => clearTimeout(t);
  }, [travel, gameState]);

  // Hard timeout so a stuck join never traps the user under the overlay.
  useEffect(() => {
    if (!travel || travel.error) return;
    const t = setTimeout(() => {
      setTravel((cur) => (cur && !cur.error ? { ...cur, error: 'That room didn’t respond. Try again.' } : cur));
      setTimeout(() => setTravel(null), 2500);
    }, 30000);
    return () => clearTimeout(t);
  }, [travel]);

  // ---- Incoming envelope subscription ----
  useEffect(() => {
    const unsub = signalRService.on<[string]>(HubEvents.SocialEventReceived, (raw) => {
      let env: { type?: string; inviteId?: string; roomId?: string; roomName?: string; reason?: string; from?: { displayName?: string }; expiresAtTs?: number };
      try { env = typeof raw === 'string' ? JSON.parse(raw) : (raw as typeof env); } catch { return; }
      if (!env?.type?.startsWith('room_invite')) return;

      switch (env.type) {
        case 'room_invite':
          if (!env.inviteId || !env.roomId) return;
          setIncomingInvite({
            inviteId: env.inviteId,
            roomId: env.roomId,
            roomName: env.roomName || 'a room',
            fromName: env.from?.displayName || 'Someone',
            expiresAt: env.expiresAtTs || (Date.now() + 45000),
          });
          break;
        case 'room_invite_go':
          if (env.roomId) void startTravel(env.roomId, env.roomName || 'a room');
          break;
        case 'room_invite_decline':
          showNote('They declined the invite.');
          break;
        case 'room_invite_failed':
          setIncomingInvite(null);
          if (env.reason === 'room_full') showNote('That room is full.');
          else showNote('Couldn’t set up the trip.');
          setTravel((cur) => (cur ? { ...cur, error: 'That room is unavailable.' } : cur));
          if (travel) setTimeout(() => setTravel(null), 2200);
          break;
      }
    });
    return () => { unsub(); };
    // startTravel/showNote are stable; travel read via closure is fine for the toast branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTravel, showNote]);

  // Auto-expire the incoming banner at its TTL.
  useEffect(() => {
    if (!incomingInvite) return;
    const ms = Math.max(0, incomingInvite.expiresAt - Date.now());
    const t = setTimeout(() => setIncomingInvite(null), ms);
    return () => clearTimeout(t);
  }, [incomingInvite]);

  // ---- Actions for the UI ----
  const sendInviteToRoom = useCallback(async (targetUserId: string, targetRoomId: string, targetRoomName: string) => {
    const room = currentRoomRef.current;
    if (!room) return;
    const resp = await roomInviteService.sendInvite(room.id, targetUserId, targetRoomId, targetRoomName);
    if (!resp.ok) showNote('Couldn’t send that invite.');
    else showNote(`Invite sent — waiting for a reply…`);
  }, [showNote]);

  const respondInvite = useCallback(async (accept: boolean) => {
    const inv = incomingInvite;
    const room = currentRoomRef.current;
    setIncomingInvite(null);
    if (!inv || !room) return;
    await roomInviteService.respondToInvite(room.id, inv.inviteId, accept);
    // Accept → the server sends room_invite_go which triggers startTravel.
  }, [incomingInvite]);

  return { incomingInvite, travel, note, sendInviteToRoom, respondInvite };
}
